import { useState, useEffect } from 'react';
import { useDebugTheme } from './useDebugTheme';
import { useSaveScenario } from '@/hooks/Scenarios/useSave';
import { LuCopy, LuSave, LuCheck } from 'react-icons/lu';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { RxCross1 } from 'react-icons/rx';

interface ScenarioSaverModalProps {
  isOpen: boolean;
  onClose: () => void;
  scenarioTxt: string;
  onLoad: (scenarioText: string) => void;
}

export const ScenarioSaverModal = ({
  isOpen,
  onClose,
  scenarioTxt,
  onLoad,
}: ScenarioSaverModalProps) => {
  const { isDark } = useDebugTheme();
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState(
    'Scenario ' +
      new Date().toLocaleDateString('en-US', { timeZone: 'Europe/Paris' }) +
      ' ' +
      new Date().toLocaleTimeString('en-US', {
        timeZone: 'Europe/Paris',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
  );
  const [description, setDescription] = useState('');
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const {
    save,
    scenarioTxt: hookScenarioTxt,
    isSaving,
    error,
    savedScenario,
  }: ReturnType<typeof useSaveScenario> = useSaveScenario(isOpen);

  useEffect(() => {
    if (
      savedScenario &&
      typeof savedScenario === 'object' &&
      'id' in savedScenario &&
      savedScenario.id === 'preview' &&
      savedScenario.description &&
      !description
    ) {
      setDescription(savedScenario.description);
    }
  }, [savedScenario, description]);

  useEffect(() => {
    if (
      !isSaving &&
      savedScenario &&
      typeof savedScenario === 'object' &&
      'id' in savedScenario &&
      savedScenario.id !== 'preview'
    ) {
      const savedScenarioJson = JSON.stringify(savedScenario, null, 2);
      onLoad(savedScenarioJson);
      onClose();
    }
  }, [isSaving, savedScenario, onLoad, onClose]);

  const handleSave = () => {
    if (!name.trim()) {
      return;
    }
    save(name);
  };

  const handleCopy = async () => {
    const currentScenarioTxt = hookScenarioTxt || scenarioTxt;
    if (currentScenarioTxt) {
      try {
        await navigator.clipboard.writeText(currentScenarioTxt);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
          onClose();
        }, 1000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    }
  };

  const handleClose = () => {
    setCopied(false);
    setName(
      'Scenario ' +
        new Date().toLocaleDateString('en-US', { timeZone: 'Europe/Paris' }) +
        ' ' +
        new Date().toLocaleTimeString('en-US', {
          timeZone: 'Europe/Paris',
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
    );
    setDescription('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className={`max-w-4xl max-h-[90dvh] rounded-lg ${
          isDark
            ? 'bg-slate-900 shadow-2xl shadow-black/50'
            : 'bg-white shadow-2xl shadow-black/20'
        } border-0`}
      >
        {/* Header */}
        <div
          className={`flex items-start justify-between rounded-t border-b p-5 ${
            isDark ? 'border-slate-700' : 'border-slate-200'
          }`}
        >
          <h3
            className={`text-xl font-medium ${
              isDark ? 'text-slate-100' : 'text-slate-900'
            }`}
          >
            Save Scenario Preview
          </h3>
          <DialogClose
            className={`ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm cursor-pointer ${
              isDark
                ? 'text-slate-400 hover:bg-slate-700 hover:text-slate-100'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
            }`}
            onClick={handleClose}
          >
            <RxCross1 className="h-5 w-5" />
          </DialogClose>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-4">
            {error && (
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[var(--dbg-text-muted)] mb-2">
                Scenario Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--dbg-border-input)] bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 placeholder:text-[var(--dbg-text-dim)]"
                placeholder="Enter scenario name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--dbg-text-muted)] mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--dbg-border-input)] bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-vertical placeholder:text-[var(--dbg-text-dim)]"
                placeholder="Enter scenario description (optional)"
                rows={20}
              />
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowJsonPreview(!showJsonPreview)}
                className="flex items-center text-sm font-medium text-[var(--dbg-text-secondary)] hover:text-[var(--dbg-text)] mb-2"
              >
                <svg
                  className={`w-4 h-4 mr-1 transition-transform ${
                    showJsonPreview ? 'rotate-90' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                Scenario Preview JSON
              </button>
              {showJsonPreview && (
                <pre className="bg-[var(--dbg-bg-code)] border border-[var(--dbg-border)] rounded-lg p-4 text-sm font-mono text-[var(--dbg-text)] whitespace-pre-wrap overflow-auto max-h-48">
                  {hookScenarioTxt || scenarioTxt}
                </pre>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className={`flex items-center space-x-2 rounded-b p-6 border-t ${
            isDark ? 'border-slate-700' : 'border-slate-200'
          }`}
        >
          <button
            onClick={handleCopy}
            disabled={!hookScenarioTxt && !scenarioTxt}
            className="flex-1 border py-1 text-[#2C1696] focus:ring-0 focus:outline-hidden rounded-lg cursor-pointer h-10 flex items-center justify-center gap-1 disabled:opacity-50"
          >
            {copied ? (
              <>
                <LuCheck className="size-4" /> Copied!
              </>
            ) : (
              <>
                <LuCopy className="size-4" /> Copy
              </>
            )}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="flex-1 border py-1 bg-[#2C1696] text-white focus:ring-0 focus:outline-hidden rounded-lg cursor-pointer h-10 flex items-center justify-center gap-1 disabled:opacity-50"
          >
            <LuSave className="size-4" />
            {isSaving ? 'Saving...' : 'Save to Database'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
